import React, { useState } from "react";
import * as engineService from "@/shared/api/services/engineService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  X,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import MerchantForm from "@/shared/components/shared/MerchantForm";
import { getProviderName } from "@/shared/utils";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Inactive" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};

const providers = [
  { code: "SCB", name: "ธนาคารไทยพาณิชย์", nameEn: "Siam Commercial Bank" },
  { code: "KBANK", name: "ธนาคารกสิกรไทย", nameEn: "Kasikorn Bank" },
  { code: "BBL", name: "ธนาคารกรุงเทพ", nameEn: "Bangkok Bank" },
  { code: "KTB", name: "ธนาคารกรุงไทย", nameEn: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "ทรูมันนี่ วอลเล็ท", nameEn: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "พร้อมเพย์ QR", nameEn: "PromptPay QR" },
];

function generateApiKey() {
  return "pk_" + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

export default function EngineMerchants() {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState(null);
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();

  const resellerName = "Gaming Partners Ltd";

  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ["engine-merchants"],
    queryFn: () => engineService.getMyMerchants(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => engineService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-merchants"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => engineService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-merchants"]);
      setFormOpen(false);
      setSelectedMerchant(null);
      setSuspendDialog(null);
    },
  });

  const filteredMerchants = merchants.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const getTodayVolume = (merchantCode) => {
    // TODO: Implement transaction volume query from backend
    // For now, return placeholder value
    return 0;
  };

  const handleSave = async (data) => {
      if (selectedMerchant) {
        const updateData = {
          name: data.name,
          status: data.status,
          currency: data.currency,
          ipWhitelist: data.ipWhitelist || undefined,
          fee_type: data.fee_type || "PERCENTAGE",
          fee_rate: Number(data.fee_rate) || 0,
        };
        await updateMutation.mutateAsync({ id: selectedMerchant.id, data: updateData });
      } else {
        const createData = {
          email: data.email,
          password: data.password,
          name: data.name,
          currency: data.currency || "THB",
          webhook: data.webhook || undefined,
          callback: data.callback || undefined,
          ipWhitelist: data.ipWhitelist || undefined,
          fee_type: data.fee_type || "PERCENTAGE",
          fee_rate: Number(data.fee_rate) || 0,
        };
        await createMutation.mutateAsync(createData);
      }
  };

  const handleToggleStatus = async () => {
    if (!suspendDialog) return;
    const currentStatus = suspendDialog.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({
      id: suspendDialog.id,
      data: { name: suspendDialog.name, status: newStatus },
    });
  };

  return (
    <EngineLayout currentPage="EngineMerchants" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("merchants")}</h1>
            <p className="text-slate-500">{merchants.length} {t("merchantsRegisteredCount")}</p>
          </div>
          <Button onClick={() => setFormOpen(true)} className="gap-2 bg-sky-600 hover:bg-sky-700">
            <Plus className="h-4 w-4" />
            {t("addMerchant")}
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("searchMerchantsPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">{t("merchant")}</TableHead>
                <TableHead className="font-semibold">{t("code")}</TableHead>
                <TableHead className="font-semibold">{t("status")}</TableHead>
                <TableHead className="font-semibold">{t("currency")}</TableHead>
                <TableHead className="font-semibold">Fee</TableHead>

                <TableHead className="font-semibold">{t("created")}</TableHead>
                <TableHead className="font-semibold w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    {t("loading")}
                  </TableCell>
                </TableRow>
              ) : filteredMerchants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    {t("noMerchantsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredMerchants.map((merchant, index) => {
                  const status = statusConfig[merchant.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  const todayVolume = getTodayVolume(merchant.code);

                  return (
                    <motion.tr
                      key={merchant.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.02 * index }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-sky-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{merchant.name}</p>
                            <p className="text-sm text-slate-500">{merchant.contact_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{merchant.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${status.color} gap-1 border-0`}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{merchant.currency || "THB"}</Badge>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {merchant.fee_rate ?? "2.5"}{merchant.fee_type === "FIXED" ? "" : "%"}
                      </TableCell>

                      <TableCell className="text-slate-500">
                        {merchant.created_date ? format(new Date(merchant.created_date), "MMM d, yyyy") : "N/A"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedMerchant(merchant);
                              setFormOpen(true);
                            }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t("edit")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setSuspendDialog(merchant)}>
                              {merchant.status === "active" ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2" />
                                  {t("suspend")}
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2" />
                                  {t("activate")}
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Merchant Form Modal */}
        <MerchantForm
          merchant={selectedMerchant}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setSelectedMerchant(null);
          }}
          onSave={handleSave}
          role="engine"
        />

        {/* Suspend Confirmation */}
        <AlertDialog open={!!suspendDialog} onOpenChange={() => setSuspendDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {suspendDialog?.status === "active" ? t("suspendMerchantTitle") : t("activateMerchantTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {suspendDialog?.status === "active"
                  ? `${t("suspendMerchantConfirm")} "${suspendDialog?.name}"? ${t("willNotProcessTransactions")}`
                  : `${t("activateMerchantConfirm")} "${suspendDialog?.name}"?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleToggleStatus}
                className={suspendDialog?.status === "active" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {suspendDialog?.status === "active" ? t("suspend") : t("activate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </EngineLayout>
  );
}
